import type { ImportStoriesResp, ImportTopicsResp } from '../../../api/schemas.js';

/** The `pickerId`s of the Settings → Data import triggers (NM-YGJFNY). */
export const IMPORT_TOPICS_PICKER = 'import-topics';
export const IMPORT_STORIES_PICKER = 'import-stories';

export interface ImportFileDeps {
  importTopics: (fileText: string) => Promise<ImportTopicsResp>;
  importStories: (fileText: string) => Promise<ImportStoriesResp>;
  showToast: (message: string) => void;
}

/**
 * Read a file chosen through one of the import triggers (FR-30.5–30.14).
 *
 * `wireFilePickers` reports the selection and resets the input afterwards, so
 * choosing the same file again reports again (NM-YGJFNY). The outcome is always
 * a toast (FR-30.7): a bulk action whose result you cannot see invites running
 * it twice. Story import names the topics it had to create (FR-30.12), so that
 * is not a surprise discovered later in the sidebar.
 *
 * Resolves once the toast is shown; a picker this does not own, or an empty
 * selection, is ignored.
 */
export async function importPickedFile(
  pickerId: string | undefined,
  files: readonly File[],
  deps: ImportFileDeps,
): Promise<void> {
  const file = files.at(0);
  if (file === undefined) return;
  if (pickerId !== IMPORT_TOPICS_PICKER && pickerId !== IMPORT_STORIES_PICKER) return;
  try {
    const text = await file.text();
    deps.showToast(
      pickerId === IMPORT_TOPICS_PICKER
        ? topicsReport(await deps.importTopics(text))
        : storiesReport(await deps.importStories(text)),
    );
  } catch (err) {
    deps.showToast(`Import failed: ${err instanceof Error ? err.message : String(err)}`);
  }
}

function topicsReport({ added, skipped }: ImportTopicsResp): string {
  const parts = [`Added ${String(added.length)} topic${added.length === 1 ? '' : 's'}`];
  if (skipped.length > 0) parts.push(`skipped ${String(skipped.length)} you already follow`);
  return parts.join(' · ');
}

function storiesReport({ added, skipped, topicsCreated }: ImportStoriesResp): string {
  const parts = [`Added ${String(added)} stor${added === 1 ? 'y' : 'ies'}`];
  if (skipped > 0) parts.push(`skipped ${String(skipped)} already here`);
  if (topicsCreated.length > 0) {
    parts.push(`created ${String(topicsCreated.length)} topic${topicsCreated.length === 1 ? '' : 's'}`);
  }
  return parts.join(' · ');
}
